import { useEffect, useCallback } from 'react';
import { downloadTaskManager, DownloadTask, DownloadTaskStatus } from '@/utils/downloadTaskManager';
import useGlobalStore from '@/store/useGlobalStore';

/**
 * 下载任务管理 Hook
 * 用于在组件中管理和监听下载任务状态
 */
export const useDownloadTaskManager = () => {
    const { setDownloadTasks, setDownloadTasksCount } = useGlobalStore();

    // 任务状态更新回调
    const handleTasksUpdate = useCallback((tasks: DownloadTask[]) => {
        setDownloadTasks(tasks);
        const activeCount = tasks.filter(
            task => task.status === DownloadTaskStatus.PENDING || task.status === DownloadTaskStatus.DOWNLOADING
        ).length;
        setDownloadTasksCount(activeCount);
    }, [setDownloadTasks, setDownloadTasksCount]);

    // 组件挂载时添加监听器
    useEffect(() => {
        downloadTaskManager.addListener(handleTasksUpdate);

        // 组件卸载时移除监听器
        return () => {
            downloadTaskManager.removeListener(handleTasksUpdate);
        };
    }, [handleTasksUpdate]);

    // 检查文件是否正在下载
    const isFileDownloading = useCallback((fileKey: string) => {
        return downloadTaskManager.isFileDownloading(fileKey);
    }, []);

    // 添加并执行下载任务
    const startDownload = useCallback(async (
        fileKey: string,
        fileName: string,
        downloadUrl: string,
        options?: {
            method?: 'GET' | 'POST';
            headers?: Record<string, string>;
            withCredentials?: boolean;
        }
    ) => {
        // 添加任务
        const taskId = downloadTaskManager.addTask(fileKey, fileName, downloadUrl);
        if (!taskId) {
            return false; // 已在下载中
        }

        // 执行下载
        return downloadTaskManager.downloadWithProgress(taskId, downloadUrl, fileName, options);
    }, []);

    // 移除任务
    const removeTask = useCallback((taskId: string) => {
        downloadTaskManager.removeTask(taskId);
    }, []);

    // 清除已完成的任务
    const clearCompletedTasks = useCallback(() => {
        downloadTaskManager.clearCompletedTasks();
    }, []);

    // 取消下载任务
    const cancelDownload = useCallback((taskId: string) => {
        downloadTaskManager.cancelDownload(taskId);
    }, []);

    // 获取所有任务
    const getAllTasks = useCallback(() => {
        return downloadTaskManager.getAllTasks();
    }, []);

    // 获取进行中的任务数量
    const getActiveTasksCount = useCallback(() => {
        return downloadTaskManager.getActiveTasksCount();
    }, []);

    return {
        isFileDownloading,
        startDownload,
        removeTask,
        clearCompletedTasks,
        cancelDownload,
        getAllTasks,
        getActiveTasksCount
    };
};

export default useDownloadTaskManager;

